import { create } from 'zustand';
import { Preferences } from '@capacitor/preferences';

type HealthZone = 'CRITICAL' | 'WARNING' | 'HEALTHY' | 'EXCELLENT';

interface User {
  id: string;
  email: string;
  fullName: string;
  familyStatus: string;
  tier: string;
  healthScore: number;
  healthScoreZone: HealthZone;
  createdAt: string;
  updatedAt: string;
  gender?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  isHydrated: boolean;
  setUser: (user: User | null, isGuest?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  updateUserField: (field: string, value: any) => Promise<void>;
  hydrate: () => Promise<void>;
}

// ============================================================
// CUSTOM PERSISTENCE using Capacitor Preferences (iOS-safe)
// ============================================================
const STORAGE_KEY = 'nurwallet-auth-storage';

const saveAuthState = async (state: { user: User | null; isAuthenticated: boolean; isGuest: boolean }) => {
  try {
    await Preferences.set({
      key: STORAGE_KEY,
      value: JSON.stringify(state)
    });
    console.log('[AuthStore] Saved state to Preferences:', { isAuthenticated: state.isAuthenticated, userId: state.user?.id });
  } catch (e) {
    console.warn('[AuthStore] Failed to save to Preferences, trying localStorage fallback', e);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      console.log('[AuthStore] Saved state to localStorage fallback');
    } catch (fallbackError) {
      console.error('[AuthStore] Both Preferences and localStorage failed', fallbackError);
    }
  }
};

const loadAuthState = async () => {
  try {
    // First try Capacitor Preferences (encrypted on iOS, reliable)
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (value) {
      const parsed = JSON.parse(value);
      console.log('[AuthStore] Loaded state from Preferences:', { isAuthenticated: parsed?.isAuthenticated, userId: parsed?.user?.id });
      return parsed;
    }
  } catch (e) {
    console.warn('[AuthStore] Failed to load from Preferences', e);
  }

  // Fallback to localStorage for web development
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('[AuthStore] Loaded state from localStorage fallback:', { isAuthenticated: parsed?.isAuthenticated, userId: parsed?.user?.id });
      return parsed;
    }
  } catch (e) {
    console.warn('[AuthStore] Failed to load from localStorage', e);
  }

  console.log('[AuthStore] No stored auth state found');
  return null;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isGuest: false,
  isHydrated: false,

  setUser: async (user, isGuest = false) => {
    const newState = { 
      user, 
      isAuthenticated: !!user,
      isGuest,
      isHydrated: true
    };
    
    console.log('[AuthStore] setUser called:', { isAuthenticated: newState.isAuthenticated, isGuest, userId: user?.id });
    
    // Update store immediately for UI responsiveness
    set(newState);
    
    // Persist asynchronously
    await saveAuthState(newState);
  },

  logout: async () => {
    console.log('[AuthStore] logout called');
    
    // CRITICAL: Clear global user ID to prevent stale session on re-login
    if (typeof window !== 'undefined') {
      (window as any).__NURWALLET_USER_ID__ = null;
      delete (window as any).__NURWALLET_USER_ID__;
    }

    const newState = {
      user: null,
      isAuthenticated: false,
      isGuest: false,
      isHydrated: true
    };

    set(newState);
    
    // Clear all auth-related storage (Preferences + localStorage fallback)
    const keysToRemove = [
      STORAGE_KEY,
      'nurwallet_session',
      'accessToken',
      'current_user_id',
      'user',
      'user_tier',
      'auth_method',
      'auth_token',
      'user_id',
    ];

    try {
      await Promise.all(keysToRemove.map(key => Preferences.remove({ key })));
      keysToRemove.forEach(key => {
        try { localStorage.removeItem(key); } catch {}
      });
    } catch (e) {
      console.warn('[AuthStore] Failed to clear storage on logout', e);
    }
  },

  updateUserField: async (field, value) => {
    const { user } = get();
    if (!user) return;

    const updatedUser = { ...user, [field]: value };
    const newState = {
      user: updatedUser,
      isAuthenticated: true,
      isGuest: get().isGuest,
      isHydrated: true
    };

    set(newState);
    await saveAuthState(newState);
  },

  hydrate: async () => {
    const stored = await loadAuthState();
    if (stored) {
      set({ 
        ...stored, 
        isHydrated: true 
      });
    } else {
      set({ isHydrated: true });
    }
  }
}));

// ============================================================
// AUTO-HYDRATE on app init (call this in main.tsx or App.tsx)
// NOTE: Always re-hydrate when called — do NOT use a singleton
// guard. In iOS Capacitor the webview can remount without the
// module reloading, causing a skipped hydration and leaving the
// store stuck with isHydrated=false / user=null.
// ============================================================
export const initializeAuthStore = async () => {
  try {
    await useAuthStore.getState().hydrate();
  } catch (error) {
    console.error('[AuthStore] Hydration failed', error);
    // Set hydrated flag anyway to prevent infinite loops / stuck UI
    useAuthStore.setState({ isHydrated: true });
  }
};

export default useAuthStore;